import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useWorkflow } from '../../context/WorkflowContext';
import { HelpCircle, CheckCircle, Edit3, ChevronLeft, ChevronRight, Award, RefreshCw, ArrowLeft, Target, Clock, Home, RotateCcw, X } from 'lucide-react';
import QuestionsScene from '../3D/QuestionsScene';
import FeedbackModal from '../FeedbackModal';
import PacManChaseBar, {
  DIFFICULTY_CONFIG,
  PACMAN_START,
  PACMAN_STEP,
  getCatchThreshold,
} from '../PacManChaseBar';
import DifficultySelect from '../DifficultySelect';
import { parseQuestionsContent } from '../../utils/contentParser';
import ReactMarkdown from 'react-markdown';

const getCorrectLetter = (qData) =>
  (qData?.correctAnswer || qData?.answer || 'A').toString().trim().toUpperCase().charAt(0);

const StageContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const SceneContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 1;
`;

const ContentPanel = styled(motion.div)`
  width: 100%;
  max-width: 800px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2rem;
  overflow-y: auto;
  position: relative;
  z-index: 10;
  margin: 2rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 2rem;
  color: #333;
`;

const ContentTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0;
  color: #333;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  margin-bottom: 2rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ProgressBarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const ProgressBarTitle = styled.h3`
  color: #333;
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
`;

const ProgressBarScore = styled.span`
  color: #4ade80;
  font-size: 1.1rem;
  font-weight: 700;
`;

const ProgressBarTrack = styled.div`
  width: 100%;
  height: 12px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  overflow: hidden;
  position: relative;
`;

const ProgressBarFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #4ade80 0%, #22c55e 100%);
  border-radius: 6px;
  position: relative;
`;

const ProgressBarText = styled.div`
  color: #666;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  text-align: center;
`;

const QuestionControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const ControlButton = styled(motion.button)`
  background: rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.3);
  color: #333;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuestionCounter = styled.span`
  color: #666;
  font-size: 0.9rem;
  font-weight: 500;
`;

const QuestionContainer = styled(motion.div)`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 15px;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const QuestionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 1rem;
  color: #4ade80;
  font-weight: 600;
`;

const QuestionText = styled.div`
  color: #333;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const Option = styled(motion.div)`
  padding: 1rem;
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    border-color: rgba(0, 0, 0, 0.4);
  }

  &.selected {
    background: rgba(74, 144, 226, 0.2);
    border-color: #4A90E2;
  }

  &.correct {
    background: rgba(74, 222, 128, 0.2);
    border-color: #4ADE80;
  }

  &.incorrect {
    background: rgba(239, 68, 68, 0.2);
    border-color: #EF4444;
  }
`;

const OptionLetter = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${props => 
    props.$correct ? '#4ADE80' :
    props.$incorrect ? '#EF4444' :
    props.$selected ? '#4A90E2' :
    'rgba(0, 0, 0, 0.2)'
  };
  color: ${props => 
    props.$correct ? 'white' :
    props.$incorrect ? 'white' :
    props.$selected ? 'white' :
    '#333'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
`;

const OptionText = styled.div`
  color: #333;
  flex: 1;
`;

const Explanation = styled(motion.div)`
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(74, 144, 226, 0.1);
  border: 1px solid rgba(74, 144, 226, 0.3);
  border-radius: 10px;
  color: #333;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled(motion.button)`
  flex: 1;
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);

  &.approve {
    background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
    color: white;
  }

  &.request-changes {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    color: white;
  }

  &.back {
    background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
    color: white;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const CompletionScreen = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  z-index: 1000;
  padding: 2rem;
  overflow-y: auto;
  padding-top: 10vh;
`;

const CompletionContent = styled(motion.div)`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  margin-bottom: 2rem;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.5);
  }
`;


const MedalContainer = styled.div`
  width: 300px;
  height: 300px;
  margin: 0 auto 2rem;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* Medal base - golden circular medal */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 200px;
    height: 200px;
    background: linear-gradient(135deg, #ffd700 0%, #ffb347 50%, #ff8c00 100%);
    border-radius: 50%;
    box-shadow: 
      0 8px 20px rgba(0, 0, 0, 0.3),
      inset 0 2px 4px rgba(255, 255, 255, 0.3),
      inset 0 -2px 4px rgba(0, 0, 0, 0.2);
    z-index: 2;
  }
  
  /* Medal inner ring */
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 160px;
    height: 160px;
    background: linear-gradient(135deg, #ffed4e 0%, #ffd700 50%, #ffb347 100%);
    border-radius: 50%;
    box-shadow: 
      inset 0 2px 4px rgba(255, 255, 255, 0.4),
      inset 0 -2px 4px rgba(0, 0, 0, 0.1);
    z-index: 3;
  }
`;

const MedalCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120px;
  height: 120px;
  background: radial-gradient(circle at 30% 30%, #ffed4e 0%, #ffd700 50%, #ffb347 100%);
  border-radius: 50%;
  box-shadow: 
    inset 0 2px 8px rgba(255, 255, 255, 0.4),
    inset 0 -2px 8px rgba(0, 0, 0, 0.2),
    0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 4;
  
  /* Highlight effect */
  &::before {
    content: '';
    position: absolute;
    top: 20%;
    right: 20%;
    width: 25px;
    height: 25px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const MedalLaurelWreath = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 280px;
  height: 280px;
  z-index: 1;
  
  /* Left laurel branch - wraps around left side */
  &::before {
    content: '';
    position: absolute;
    left: -20px;
    top: 50%;
    transform: translateY(-50%) rotate(-45deg);
    width: 80px;
    height: 120px;
    background: linear-gradient(135deg, #228b22 0%, #32cd32 50%, #90ee90 100%);
    border-radius: 50% 0 50% 0;
    box-shadow: 
      inset 0 2px 4px rgba(255, 255, 255, 0.3),
      inset 0 -2px 4px rgba(0, 0, 0, 0.2),
      0 4px 8px rgba(0, 0, 0, 0.2);
  }
  
  /* Right laurel branch - wraps around right side */
  &::after {
    content: '';
    position: absolute;
    right: -20px;
    top: 50%;
    transform: translateY(-50%) rotate(45deg);
    width: 80px;
    height: 120px;
    background: linear-gradient(135deg, #228b22 0%, #32cd32 50%, #90ee90 100%);
    border-radius: 0 50% 0 50%;
    box-shadow: 
      inset 0 2px 4px rgba(255, 255, 255, 0.3),
      inset 0 -2px 4px rgba(0, 0, 0, 0.2),
      0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const MedalLabel = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: #8b4513;
  font-size: 1.2rem;
  font-weight: 900;
  letter-spacing: 1px;
  text-shadow: 
    2px 2px 4px rgba(0, 0, 0, 0.3),
    0 0 8px rgba(255, 215, 0, 0.5);
  z-index: 5;
  text-align: center;
  background: linear-gradient(135deg, #8b4513 0%, #cd853f 50%, #daa520 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CompletionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
`;

const CompletionMessage = styled.p`
  font-size: 1.2rem;
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const CompletionButton = styled(motion.button)`
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(74, 222, 128, 0.3);
  flex: 1;
  min-width: 200px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(74, 222, 128, 0.4);
  }
`;

const CompletionQuitButton = styled(motion.button)`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(239, 68, 68, 0.3);
  flex: 1;
  min-width: 200px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(239, 68, 68, 0.4);
  }
`;

const CompletionButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;
`;

const RefreshButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  z-index: 1000;
  box-shadow: 0 4px 15px rgba(74, 222, 128, 0.4);
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(74, 222, 128, 0.5);
  }

  &:active {
    transform: translateY(0);
  }
`;

const GameOverOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(6px);
  padding: 2rem;
`;

const GameOverPanel = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 2.5rem;
  max-width: 420px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  border: 3px solid #ef4444;
`;

const GameOverTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #ef4444;
  margin: 0 0 1rem;
`;

const GameOverMessage = styled.p`
  color: #555;
  font-size: 1rem;
  line-height: 1.6;
  margin: 0 0 2rem;
`;

const GameOverButton = styled(motion.button)`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  padding: 0.9rem 2rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.4);
`;

const QuestionsStage = () => {
  const navigate = useNavigate();
  const { 
    questionsContent, 
    sessionId, 
    isProcessing, 
    requestChanges,
    setFeedbackText,
    feedbackText,
    isRequestingChanges,
    isApproving,
    setRequestingChanges,
    setApproving,
    reloadContent
  } = useWorkflow();
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);
  const [showFinalCompletionScreen, setShowFinalCompletionScreen] = useState(false);
  const [showRefreshButton, setShowRefreshButton] = useState(() => {
    return localStorage.getItem('showRefreshButton_questions') === 'true';
  });

  // Ensure button appears when request completes
  useEffect(() => {
    if (!isRequestingChanges && localStorage.getItem('showRefreshButton_questions') === 'true') {
      setShowRefreshButton(true);
    }
  }, [isRequestingChanges]);

  const [completionType, setCompletionType] = useState('excellent-performance');
  
  // Progress tracking state
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState(new Set());

  // Pac-Man chase game state
  const [difficulty, setDifficulty] = useState(null);
  const [pacManPosition, setPacManPosition] = useState(PACMAN_START);
  const [gameOutcome, setGameOutcome] = useState(null);
  const pacManPositionRef = useRef(PACMAN_START);

  // Auto-reload content if missing
  useEffect(() => {
    if (sessionId && !questionsContent && !isProcessing) {
      reloadContent();
    }
  }, [sessionId, questionsContent, isProcessing, reloadContent]);

  // Reset component state when questionsContent changes
  useEffect(() => {
    if (questionsContent) {
      setRequestingChanges(false);
      setApproving(false);
    }
  }, [questionsContent, setRequestingChanges, setApproving]);

  const questions = useMemo(() => {
    const parsedQuestions = parseQuestionsContent(questionsContent || '');
    
    // If no questions were parsed, create fallback questions
    if (parsedQuestions.length === 0) {
      return [
        {
          question: 'What is the main topic discussed in the content?',
          options: [
            'The content covers educational concepts',
            'The content discusses technical details',
            'The content focuses on practical applications',
            'The content is about theoretical frameworks'
          ],
          answer: 'A',
          explanation: 'This is a sample question created when the content could not be parsed into specific questions.'
        },
        {
          question: 'Which of the following best describes the learning approach?',
          options: [
            'Interactive and engaging',
            'Traditional and structured',
            'Visual and multimedia',
            'Text-based and comprehensive'
          ],
          answer: 'A',
          explanation: 'This is another sample question to test the UI functionality.'
        },
        {
          question: 'What should be the next step in the learning process?',
          options: [
            'Review the content thoroughly',
            'Move to the next topic',
            'Practice with exercises',
            'Take a break and return later'
          ],
          answer: 'C',
          explanation: 'This is a third sample question to ensure the UI works properly.'
        }
      ];
    }
    
    return parsedQuestions;
  }, [questionsContent]);

  const doorPosition = difficulty ? DIFFICULTY_CONFIG[difficulty].doorPosition : 60;
  const progress = questions.length > 0 ? (correctAnswers / questions.length) * 100 : 0;

  useEffect(() => {
    if (!difficulty || !sessionId || gameOutcome === 'escaped' || gameOutcome === 'caught') return;
    if (progress >= doorPosition) {
      setGameOutcome('escaped');
    }
  }, [progress, doorPosition, difficulty, gameOutcome, sessionId]);

  const resetChase = () => {
    pacManPositionRef.current = PACMAN_START;
    setPacManPosition(PACMAN_START);
    setGameOutcome(null);
  };

  const advancePacMan = (currentProgress) => {
    if (gameOutcome === 'escaped' || gameOutcome === 'caught') return;
    if (currentProgress >= doorPosition) return;

    const nextPac = pacManPositionRef.current + PACMAN_STEP;
    pacManPositionRef.current = nextPac;
    setPacManPosition(nextPac);

    const avatarPosition = getCatchThreshold(currentProgress, doorPosition);
    if (nextPac >= avatarPosition) {
      setGameOutcome('caught');
    }
  };

  const handleDifficultySelect = (level) => {
    setDifficulty(level);
    resetChase();
    setCorrectAnswers(0);
    setTotalAnswered(0);
    setAnsweredQuestions(new Set());
    setCurrentQuestion(0);
    setSelectedOption(null);
    setShowExplanation(false);
  };

  const handleRetryChase = () => {
    resetChase();
    setCorrectAnswers(0);
    setTotalAnswered(0);
    setAnsweredQuestions(new Set());
    setCurrentQuestion(0);
    setSelectedOption(null);
    setShowExplanation(false);
  };

  // Show proper message when no session is started
  if (!sessionId) {
    return (
      <StageContainer>
        <SceneContainer>
          <QuestionsScene 
            questionsContent={null} 
            currentQuestion={0}
          />
        </SceneContainer>
        
        <ContentPanel
          initial={{ x: 500 }}
          animate={{ x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <ContentHeader>
            <HelpCircle size={32} />
            <ContentTitle>Assessment Questions</ContentTitle>
          </ContentHeader>

          <div style={{ 
            textAlign: 'center', 
            padding: '3rem 2rem',
            color: '#666'
          }}>
            <h3 style={{ marginBottom: '1rem', color: '#333' }}>
              No Workflow Session Started
            </h3>
            <p style={{ marginBottom: '2rem', lineHeight: '1.6' }}>
              To view assessment questions, please start a workflow session first.
              <br />
              Go to the Story stage to begin the educational content generation process.
            </p>
            <Button
              onClick={() => navigate('/story')}
              style={{ 
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500'
              }}
            >
              Go to Story Stage
            </Button>
          </div>
        </ContentPanel>
      </StageContainer>
    );
  }

  const handleApprove = async () => {
    
    if (!sessionId) {
      return;
    }
    
    // Check if user has 50% or more progress to show completion screen
    if (progress >= 50) {
      // Determine completion type based on progress
      if (progress >= 90) {
        setCompletionType('excellent-performance');
      } else if (progress >= 70) {
        setCompletionType('very-good-results');
      } else {
        setCompletionType('good-effort');
      }
      setShowCompletionScreen(true);
      return;
    }
    
    // If user has less than 50% progress (no score), show final completion screen directly
    // Skip the API call for users with no score to avoid getting stuck
    setShowFinalCompletionScreen(true);
  };

  const handleBeginNewJourney = () => {
    localStorage.removeItem('workflowSessionId');
    localStorage.removeItem('workflowStartTime');
    setShowCompletionScreen(false);
    navigate('/');
  };

  const handleCloseApplication = () => {
    localStorage.removeItem('workflowSessionId');
    localStorage.removeItem('workflowStartTime');
    window.close();
  };

  const handleRequestChanges = () => {
    if (isApproving) return;
    setShowModal(true);
  };

  const handleSubmitFeedback = async (feedback = null) => {
    const feedbackToSubmit = feedback || feedbackText;
    
    if (!sessionId || !feedbackToSubmit?.trim()) {
      return;
    }
    
    try {
      setRequestingChanges(true);
      await requestChanges(sessionId, 'questions', feedbackToSubmit);
      
      // Show refresh button after changes are received - use both state and localStorage
      localStorage.setItem('showRefreshButton_questions', 'true');
      setShowRefreshButton(true);
      setShowModal(false);
      // Force a re-render to ensure button appears, then show alert
      setTimeout(() => {
        // Double-check localStorage is set
        if (localStorage.getItem('showRefreshButton_questions') === 'true') {
          setShowRefreshButton(true);
        }
        alert('Changes received. Please press the Reload Content Button.');
      }, 100);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setRequestingChanges(false);
    }
  };

  const handleRefresh = () => {
    localStorage.removeItem('showRefreshButton_questions');
    window.location.reload();
  };

  const nextQuestion = () => {
    setCurrentQuestion(prev => Math.min(prev + 1, questions.length - 1));
    setSelectedOption(null);
    setShowExplanation(false);
  };

  const prevQuestion = () => {
    setCurrentQuestion(prev => Math.max(prev - 1, 0));
    setSelectedOption(null);
    setShowExplanation(false);
  };

  const handleOptionSelect = (optionIndex) => {
    if (!difficulty || gameOutcome === 'caught') return;

    const qData = questions[currentQuestion];
    if (!qData) return;

    const selectedLetter = String.fromCharCode(65 + optionIndex);
    const correctLetter = getCorrectLetter(qData);
    const isCorrect = selectedLetter === correctLetter;
    const isFirstAttempt = !answeredQuestions.has(currentQuestion);

    setSelectedOption(optionIndex);
    setShowExplanation(true);

    if (isFirstAttempt) {
      setAnsweredQuestions(prev => new Set([...prev, currentQuestion]));
      setTotalAnswered(prev => prev + 1);
      if (isCorrect) {
        setCorrectAnswers(prev => prev + 1);
      }
    }

    if (!isCorrect && gameOutcome !== 'escaped') {
      advancePacMan(progress);
    }
  };

  const currentQuestionData = questions[currentQuestion] || {
    question: 'No question available.',
    options: ['Option A', 'Option B', 'Option C', 'Option D'],
    correctAnswer: 'A',
    explanation: 'No explanation available.'
  };

  // Helper functions for completion screen
  const getCompletionBadge = () => {
    const getLabel = () => {
      switch (completionType) {
        case 'excellent-performance':
          return 'EXCELLENT PERFORMANCE';
        case 'very-good-results':
          return 'VERY GOOD RESULTS';
        case 'good-effort':
          return 'GOOD EFFORT';
        default:
          return 'EXCELLENT PERFORMANCE';
      }
    };

    return (
      <MedalContainer>
        <MedalLaurelWreath />
        <MedalCenter />
        <MedalLabel>{getLabel()}</MedalLabel>
      </MedalContainer>
    );
  };

  const getCompletionTitle = () => {
    switch (completionType) {
      case 'excellent-performance':
        return 'Excellent Performance!';
      case 'very-good-results':
        return 'Very Good Results!';
      case 'good-effort':
        return 'Good Effort!';
      default:
        return 'Congratulations!';
    }
  };

  const getCompletionMessage = () => {
    switch (completionType) {
      case 'excellent-performance':
        return `Outstanding performance! You've achieved an excellent score of ${Math.round(progress)}%! You've demonstrated exceptional mastery of the assessment questions.`;
      case 'very-good-results':
        return `Great work! You've achieved a very good score of ${Math.round(progress)}%! You've shown strong understanding of the material.`;
      case 'good-effort':
        return `Well done! You've achieved a good score of ${Math.round(progress)}%! You've demonstrated solid effort and understanding.`;
      default:
        return `You've achieved a score of ${Math.round(progress)}%!`;
    }
  };

  return (
    <StageContainer>
      <SceneContainer>
        <QuestionsScene 
          questionsContent={questionsContent} 
          currentQuestion={currentQuestion}
        />
      </SceneContainer>

      {!difficulty && (
        <DifficultySelect onSelect={handleDifficultySelect} />
      )}

      {gameOutcome === 'caught' && (
        <GameOverOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <GameOverPanel
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
          >
            <GameOverTitle>You Lost!</GameOverTitle>
            <GameOverMessage>
              Pac-Man caught you before you reached the door!
              Answer correctly to move forward — wrong answers let Pac-Man advance.
              Try again to escape.
            </GameOverMessage>
            <GameOverButton
              onClick={handleRetryChase}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Try Again
            </GameOverButton>
          </GameOverPanel>
        </GameOverOverlay>
      )}

      {/* Refresh Button - appears after changes are received */}
      {(showRefreshButton || localStorage.getItem('showRefreshButton_questions') === 'true') && (
        <RefreshButton onClick={handleRefresh}>
          <RefreshCw size={16} />
          Reload Content
        </RefreshButton>
      )}
      
      <ContentPanel
        initial={{ x: 500 }}
        animate={{ x: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{
          opacity: difficulty && gameOutcome !== 'caught' ? 1 : 0.6,
          pointerEvents: difficulty && gameOutcome !== 'caught' ? 'auto' : 'none',
        }}
      >
        <ContentHeader>
          <HelpCircle size={32} />
          <ContentTitle>Assessment Questions</ContentTitle>
        </ContentHeader>

        <ProgressBarContainer>
          <ProgressBarHeader>
            <ProgressBarTitle>Progress</ProgressBarTitle>
            <ProgressBarScore>{Math.round(progress)}%</ProgressBarScore>
          </ProgressBarHeader>
          <ProgressBarTrack>
            <ProgressBarFill
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </ProgressBarTrack>
          <PacManChaseBar
            progressPercent={progress}
            pacManPosition={pacManPosition}
            doorPosition={doorPosition}
            gameOutcome={gameOutcome}
            difficultyLabel={difficulty ? DIFFICULTY_CONFIG[difficulty].label : 'Select difficulty'}
          />
          <ProgressBarText>
            {correctAnswers} correct answers out of {questions.length} questions
            {` (${Math.round(progress)}% progress)`}
          </ProgressBarText>
        </ProgressBarContainer>

        <QuestionControls>
          <ControlButton
            onClick={prevQuestion}
            disabled={currentQuestion === 0}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronLeft size={20} />
          </ControlButton>
          
          <QuestionCounter>
            Question {currentQuestion + 1} of {questions.length}
          </QuestionCounter>
          
          <ControlButton
            onClick={nextQuestion}
            disabled={currentQuestion === questions.length - 1}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronRight size={20} />
          </ControlButton>
        </QuestionControls>

        <QuestionContainer
          key={currentQuestion}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <QuestionHeader>
            <Award size={20} />
            Question {currentQuestion + 1}
          </QuestionHeader>
          
          <QuestionText>
            <ReactMarkdown>{currentQuestionData.question}</ReactMarkdown>
          </QuestionText>
          
          <OptionsContainer>
            {currentQuestionData.options.map((option, index) => {
              const optionLetter = String.fromCharCode(65 + index);
              const isSelected = selectedOption === index;
              const isCorrect = optionLetter === getCorrectLetter(currentQuestionData);
              const showResult = showExplanation;
              
              return (
                <Option
                  key={index}
                  className={`
                    ${isSelected ? 'selected' : ''} 
                    ${showResult && isCorrect ? 'correct' : ''} 
                    ${showResult && isSelected && !isCorrect ? 'incorrect' : ''}
                  `}
                  onClick={() => handleOptionSelect(index)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <OptionLetter
                    $correct={showResult && isCorrect}
                    $incorrect={showResult && isSelected && !isCorrect}
                    $selected={isSelected}
                  >
                    {optionLetter}
                  </OptionLetter>
                  <OptionText>
                    <ReactMarkdown>{option}</ReactMarkdown>
                  </OptionText>
                </Option>
              );
            })}
          </OptionsContainer>
          
          <AnimatePresence>
            {showExplanation && (
              <Explanation
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <strong>Explanation:</strong> {currentQuestionData.explanation}
              </Explanation>
            )}
          </AnimatePresence>
        </QuestionContainer>

        <ActionButtons>
          <Button
            className="back"
            onClick={() => navigate('/dialogues')}
            disabled={isProcessing}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft size={20} />
            Back
          </Button>

          <Button
            className="approve"
            onClick={handleApprove}
            disabled={isProcessing || isApproving}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <CheckCircle size={20} />
            {isApproving ? 'Approving...' : 'Finish'}
          </Button>
          
          <Button
            className="request-changes"
            onClick={handleRequestChanges}
            disabled={isProcessing || isRequestingChanges}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Edit3 size={20} />
            {isRequestingChanges ? 'Processing...' : 'Request Changes'}
          </Button>
        </ActionButtons>
      </ContentPanel>

      {showModal && (
        <FeedbackModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmitFeedback}
          feedbackText={feedbackText}
          setFeedbackText={setFeedbackText}
          stage="questions"
          isSubmitting={isRequestingChanges}
          setRequestingChanges={setRequestingChanges}
          reloadContent={reloadContent}
        />
      )}

      {showCompletionScreen && (
        <CompletionScreen
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <CompletionContent
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {getCompletionBadge()}
            
            <CompletionTitle>{getCompletionTitle()}</CompletionTitle>
            
            <CompletionMessage>
              {getCompletionMessage()}
            </CompletionMessage>
            
            <CompletionButtonRow>
              <CompletionButton
                onClick={handleBeginNewJourney}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <RotateCcw size={20} />
                Begin a New Journey
              </CompletionButton>
              <CompletionQuitButton
                onClick={handleCloseApplication}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <X size={20} />
                Close Application
              </CompletionQuitButton>
            </CompletionButtonRow>
          </CompletionContent>
        </CompletionScreen>
      )}

      {showFinalCompletionScreen && (
        <CompletionScreen
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <CompletionContent
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                width: '100px', 
                height: '100px', 
                background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 2rem',
                boxShadow: '0 10px 30px rgba(74, 222, 128, 0.3)'
              }}>
                <Award size={50} color="white" />
              </div>
              
              <h1 style={{ 
                fontSize: '2.5rem', 
                fontWeight: '700', 
                color: '#333', 
                marginBottom: '1rem' 
              }}>
                Workflow Complete!
              </h1>
              
              <p style={{ 
                fontSize: '1.2rem', 
                color: '#666', 
                marginBottom: '2rem', 
                lineHeight: '1.6' 
              }}>
                You have successfully completed all stages of the educational storytelling workflow. 
                Your interactive learning experience is ready!
              </p>

              <div style={{ 
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '15px',
                padding: '2rem',
                margin: '2rem 0',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <h3 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: '600', 
                  color: '#333', 
                  marginBottom: '1.5rem',
                  textAlign: 'center'
                }}>
                  Completion Metrics
                </h3>
                
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1.5rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ 
                    background: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    textAlign: 'center',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div style={{ 
                      width: '50px',
                      height: '50px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1rem'
                    }}>
                      <Target size={24} color="white" />
                    </div>
                    <div style={{ fontSize: '2rem', fontWeight: '700', color: '#333', marginBottom: '0.5rem' }}>
                      {Math.round(progress)}%
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#666', fontWeight: '500' }}>
                      Quiz Score
                    </div>
                  </div>

                  <div style={{ 
                    background: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    textAlign: 'center',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div style={{ 
                      width: '50px',
                      height: '50px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1rem'
                    }}>
                      <CheckCircle size={24} color="white" />
                    </div>
                    <div style={{ fontSize: '2rem', fontWeight: '700', color: '#333', marginBottom: '0.5rem' }}>
                      3/3
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#666', fontWeight: '500' }}>
                      Stages Completed
                    </div>
                  </div>

                  <div style={{ 
                    background: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    textAlign: 'center',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div style={{ 
                      width: '50px',
                      height: '50px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1rem'
                    }}>
                      <Clock size={24} color="white" />
                    </div>
                    <div style={{ fontSize: '2rem', fontWeight: '700', color: '#333', marginBottom: '0.5rem' }}>
                      {Math.round((Date.now() - (parseInt(localStorage.getItem('workflowStartTime')) || Date.now())) / 1000 / 60)}m
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#666', fontWeight: '500' }}>
                      Session Duration
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ 
                display: 'flex',
                gap: '1rem',
                justifyContent: 'center',
                marginTop: '2rem',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => {
                    localStorage.removeItem('workflowSessionId');
                    localStorage.removeItem('workflowStartTime');
                    navigate('/');
                  }}
                  style={{
                    padding: '1rem 2rem',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
                  }}
                >
                  <RotateCcw size={20} />
                  Start New Workflow
                </button>
                
                <button
                  onClick={() => navigate('/')}
                  style={{
                    padding: '1rem 2rem',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
                  }}
                >
                  <Home size={20} />
                  Go to Home
                </button>

                <button
                  onClick={() => {
                    localStorage.removeItem('workflowSessionId');
                    localStorage.removeItem('workflowStartTime');
                    navigate('/');
                  }}
                  style={{
                    padding: '1rem 2rem',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
                  }}
                >
                  <X size={20} />
                  Quit
                </button>
              </div>
            </div>
          </CompletionContent>
        </CompletionScreen>
      )}
    </StageContainer>
  );
};

export default QuestionsStage;